// Single source of ALL wording for the M2 "Link health records to ABHA" records list
// (redesign v2: merged OPD + IPD checklist cards, record-type strip, inline Add file / Replace panel).
//
// Source: ABDM-M2-HITYPES wording-review.md (§1 CHANGE/KEEP rows, §2 glossary) with owner
// rulings applied on top: OR-10 Replace = pencil icon only (tooltip + aria "Replace file"),
// OR-15 no date field, OR-16 no "issued by another organisation" tick (a guidance line
// instead), OR-17 no records-without-a-visit path, OR-82 no upload drawer and no camera (an inline panel).
// Invoice strings (OR-20..OR-24, INV-F1) are in INVOICE at the end. Verb is "link" / "linked", never "send" / "sent" (R168; the one exception is the link notice's "sent to ABHA, linking…", OR-114). Say "organisation".
//
// Record-type NAMES are not here: they live in HI_TYPE_LABELS (abdmHiTypes.js) - pass
// hiTypeLabel(type) into the functions below. Strings already owned by abdmCopy.js
// (Retry link, Verify ABHA, Linked, Link failed, ...) are imported, not repeated.
//
// Strings not in wording-review.md were written by the dev from an owner ruling or a plan default;
// Business ratified them all (OR-60), so none is marked for confirmation any more.
//
// REDESIGN v2 (OR-64..OR-75, build-plan-redesign.md REV 2): the new blocks / keys are marked
// "redesign v2". They follow the approved mockup (redesign-v2/variant-a-checklist-cards.html) and
// are NOT yet ratified by Business / PO - they ratify at the verdict. The OLD keys they replaced were
// removed in R-F8 (FE integration round 5) and R-F12 (the upload drawer's keys) once no screen used them.
import { RETRY_LINK, VERIFY_ABHA, LINK_STATUS, LINK_COPY } from './abdmCopy';
import { abhaStatusMeta } from '../components/visit/abhaStatusMeta';
import { hiTypeLabel, TYPE_INVOICE } from './abdmHiTypes';
import { isNotApproved, PAUSE_REASON, NOTICE_OUTCOME } from './hipRecordEnums';

const plural = (n, one, many) => (n === 1 ? one : many);

const NOTICE_NAMES_FULL = 4; // LIST.linkNoticeLine lists up to this many names in full ...
const NOTICE_NAMES_CUT = 3; // ... else only this many, then "+N more"

// Glossary row 24: one ABHA-status wording, owned by abhaStatusMeta ('LINKED' = number on file,
// not proven = what the records contract calls NOT_VERIFIED).
export const ABHA_VERIFIED = abhaStatusMeta('VERIFIED').label;
export const ABHA_NOT_VERIFIED = abhaStatusMeta('LINKED').label;

export const REPLACED_BY_STAFF = 'Replaced by staff'; // rows 21 + 28

// "Visits and admissions" is what a card is (glossary); "records" means record types.
export const visitsAndAdmissions = (n) => (n === 1 ? '1 visit or admission' : `${n} visits and admissions`);
export const fileCount = (n) => `${n} ${plural(n, 'file', 'files')}`;

// A1.6: a type can hold several slots (Diagnostic Report, Invoice), each with its own `label`
// from the server (lab test name / file title / bill number). "Diagnostic Report - CBC".
// A slot without a label (every single-instance type) shows the type name alone.
export const slotTitle = (typeLabel, label) => (label ? `${typeLabel} - ${label}` : typeLabel);
// OR-55 / QA D3: the visit's latest prescription is a draft, pending approval or rejected - nothing to
// link yet. Shown greyed as "Prescription - not approved yet" (chip + row), with the reason, no actions.
export const NOT_APPROVED = {
  tag: 'not approved yet',
  reason: "The doctor hasn't approved this prescription yet.",
};

// The one name a slot is shown under (chip, row, preview title, confirm, error lines).
export const slotName = (slot) => slotTitle(hiTypeLabel(slot.recordType), isNotApproved(slot) ? NOT_APPROVED.tag : slot.label);

// "Shivang Dhuria's visit on 30 Oct 2026" / "Ravi Menon's admission (IP/2026-2027/00008)".
// kind = 'OPD' | 'IPD'; dateText is pre-formatted (IST) by the caller.
export const encounterPhrase = ({ kind, patientName, dateText, ipNumber }) => (kind === 'IPD'
  ? `${patientName}'s admission${ipNumber ? ` (${ipNumber})` : ''}`
  : `${patientName}'s visit on ${dateText}`);

// ── Slot state -> chip / row wording (state is derived by the server, I8) ─────────────
// READY has no chip word: the chip shows the Link button instead (glossary "Ready").
export const SLOT_STATE_LABEL = {
  LINKING: LINK_STATUS.NOTIFIED, // 'Linking…'
  LINKED: LINK_STATUS.LINKED,
  LINK_FAILED: LINK_STATUS.FAILED, // 'Link failed'
  PAUSED: 'Paused', // rows 65
};

// ── List: header, filters, count line, pager (rows 2, 5-12, 66-67) ────────────────────
export const LIST = {
  // redesign v2: this page only - no "(M2)". The shared MODULE_TITLES (dashboard) keeps it (OR-70).
  pageTitle: 'Link health records to ABHA',
  subtitle: "Link a patient's health records to their ABHA.",
  from: 'From',
  to: 'To',
  presets: { today: 'Today', yesterday: 'Yesterday', last3: 'Last 3 days', last7: 'Last 7 days' },
  // OR-96 (TEMPORARY, revert with the default when bk-2256 lands): shown under the filter bar while "Last 3 days" is picked.
  last3Hint: 'Showing last 3 days. Older visits: change the dates.',
  refresh: '↻ Refresh',
  searchPlaceholder: 'Search by name, UHID or mobile',
  // Keys = contract enums, in display order. Counts keys are on the API result (`counts`).
  kinds: { ALL: 'All', OPD: 'OPD', IPD: 'IPD' },
  // The kind word in PATIENT.filtersKept (mockup: "Not linked yet · Last 3 days · All visits").
  kindsKept: { ALL: 'All visits', OPD: 'OPD', IPD: 'IPD' },
  // The order the filter offers is LIST_STATUS_OPTIONS (hipRecordEnums). NEEDS_LINKING is no longer offered (A4.1),
  // so it has no label here (the server still accepts it).
  statuses: {
    NOT_LINKED_YET: 'Not linked yet', // redesign v2 (OR-66/68): the default
    LINK_FAILED: 'Link failed', // row 10
    NOT_VERIFIED: ABHA_NOT_VERIFIED, // row 11
    LINKED: LINK_STATUS.LINKED,
    ALL: 'All statuses', // row 12
  },
  // "on 26 Oct 2026" / "from 24 Oct 2026 to 30 Oct 2026" (dates pre-formatted by the caller).
  rangeText: (from, to) => (from === to ? `on ${from}` : `from ${from} to ${to}`),
  countLine: (n, rangeText, statusLabel, statusCount) => (
    `${visitsAndAdmissions(n)} ${rangeText}${statusLabel ? ` · showing ${statusLabel} (${statusCount})` : ''}`), // row 66
  previous: '← Previous',
  next: 'Next →',
  pageOf: (page, pages, total) => `Page ${page} of ${pages} (${visitsAndAdmissions(total)})`, // row 67
  // Row 60: banner above the list when some listed patients are not ABHA-verified.
  notVerifiedBanner: (n) => `${ABHA_NOT_VERIFIED} for ${n} ${plural(n, 'patient', 'patients')}`,
  notVerifiedBannerBody: "Verify their ABHA first. Records can be linked only after the patient's ABHA is verified.",
  // Not in wording-review.md.
  loading: LINK_COPY.panelLoading, // 'Loading…'
  loadError: 'Could not load visits and admissions. Please try again.',
  empty: (rangeText) => `No visits or admissions ${rangeText}.`,
  // Date-range problems shown instead of sending the request (same words as the server's 422s).
  rangeInvalid: 'Enter valid dates.',
  rangeOrder: 'The "To" date must be on or after the "From" date.',
  rangeSpan: (days) => `Choose a date range of ${days} days or less.`,
  emptyHint: 'Try a different date range, type, status or search.',

  // ── redesign v2 (OR-65/66): one-row filters, date popover, count line ──
  statusLabel: 'Status', // aria / label of the status select
  kindLabel: 'Visit type', // aria / label of the kind select
  dateLabel: 'Date range', // aria / label of the date button and its popover
  customRange: 'Custom range…',
  apply: 'Apply', // custom range is sent only on Apply
  dateRule: 'Dates are the visit date (OPD) and the discharge date (IPD). Up to 31 days.', // inside the popover
  // "24 Oct – 31 Oct 2026" from two pre-formatted days ("24 Oct 2026"); the year is kept on both ends only when
  // they differ ("1 Nov 2025 – 31 Oct 2026"). A single day stays as it is.
  rangeDash: (from, to) => {
    if (from === to) return from;
    const year = (t) => /\s(\d{4})$/.exec(t)?.[1];
    return `${year(from) && year(from) === year(to) ? from.replace(/\s\d{4}$/, '') : from} – ${to}`;
  },
  // Text on the date button: the preset's name and the range, or the range alone for a custom range.
  dateButton: (presetLabel, rangeText) => (presetLabel ? `${presetLabel} · ${rangeText}` : rangeText),
  // Under the default filter. rangeText = rangeDash(...).
  needLinkingLine: (n, rangeText) => `${visitsAndAdmissions(n)} not linked yet · ${rangeText}`,
  // Empty state under the default filter (mockup §10). rangeText = LIST.rangeText(...) ("from 24 Oct 2026 to 31 Oct 2026").
  emptyNotLinked: (rangeText) => `No visits or admissions are waiting to be linked ${rangeText}.`,
  dismissNotice: 'Dismiss', // aria-label / tooltip of the notice's x
  // OR-114 (amends OR-93 / OR-77): after a link (card button, inline panel, invoice form) the notice says what is TRUE per record,
  // from its slot state - never "linked" before ABDM has answered. It stays until x or the next link; the list refreshes it while
  // anything is still linking. The one place the glossary's "never sent" (R168) is lifted: "sent to ABHA, linking…".
  linkNoticeState: {
    [NOTICE_OUTCOME.LINKING]: 'sent to ABHA, linking…',
    [NOTICE_OUTCOME.LINKED]: 'linked',
    [NOTICE_OUTCOME.FAILED]: 'could not be linked',
  },
  // One line of the notice: "Prescription of Shaina Dhuria: sent to ABHA, linking… · 05:14 pm". `outcome` = NOTICE_OUTCOME of the
  // records named; ...names = their names as the rows show them (slotName); timeText = formatTimeIST of the link. The list writes one
  // line per outcome (could not be linked, then linking, then linked). Up to 4 names are listed in full; more than 4: the first 3
  // and "+N more" (never "+1 more").
  linkNoticeLine: (patientName, timeText, outcome, ...names) => {
    const shown = names.length > NOTICE_NAMES_FULL
      ? `${names.slice(0, NOTICE_NAMES_CUT).join(', ')} +${names.length - NOTICE_NAMES_CUT} more`
      : names.join(', ');
    return `${shown} of ${patientName}: ${LIST.linkNoticeState[outcome]} · ${timeText}`;
  },
};

// ── Header ABDM menu (redesign v2, OR-65): a status chip that opens the registration panel + the SMS box ──
export const ABDM_MENU = {
  connected: 'Connected to ABDM',
  notConnected: 'Not connected', // also: HIP ID missing, linkage pending, status call failed (the menu says why)
  checking: 'Checking ABDM…', // before the status call returns
  heading: 'Your organisation on ABDM', // menu section 1 (the registration panel)
  smsHeading: 'ABHA app link by SMS', // menu section 2 (the SMS box); no "send" in this file (R168 guard) - the button says it
};

// OR-99: why a row is paused. ABDM-1027 = ABDM itself blocked the patient for 24 h: say so (no guessed cause). Anything else
// (a short rate-limit pause, or no reason sent) keeps the plain "Paused." sentence. `timeText` = pausedWhenIST (OR-97).
const ABDM_BLOCKED_SENTENCE = (timeText) => `ABDM has blocked linking for this patient for 24 hours. Try again after ${timeText}.`;
const pausedSentence = (timeText, pauseReason) => (pauseReason === PAUSE_REASON.ABDM_BLOCKED
  ? ABDM_BLOCKED_SENTENCE(timeText)
  : `Paused. Try again after ${timeText}.`);

// ── Encounter card (rows 14-21, 57-58, 60-62, 64-65) ──────────────────────────────────
export const CARD = {
  admittedDischarged: (admitted, discharged) => `Admitted ${admitted} → Discharged ${discharged}`,
  retryLink: RETRY_LINK,
  notVerifiedChip: ABHA_NOT_VERIFIED,
  verifyAbha: VERIFY_ABHA,
  uploadNeedsVerify: 'Verify ABHA first', // reason under a disabled Link to ABHA on the invoice form (INVOICE.needsVerify)
  notVerifiedHint: "Verify ABHA first. Records can be linked only after the patient's ABHA is verified.",
  cardError: (typeLabel) => `${typeLabel}: ${LINK_COPY.retryError}`, // row 64
  // Row 65: a paused chip/row has no live button.
  pausedNote: (whenText, pauseReason) => (pauseReason === PAUSE_REASON.ABDM_BLOCKED
    ? ABDM_BLOCKED_SENTENCE(whenText)
    : `Linking to ABHA is paused for this patient. Try again after ${whenText}.`),
  pausedReason: pausedSentence,
  // OR-97: the time in these three sentences carries the day when it is not today (IST): "tomorrow, 08:42 pm" / "03 Nov, 08:42 pm".
  pausedTomorrow: 'tomorrow',
  // The card's "Token 12" detail.
  token: (t) => `Token ${t}`,

  // ── redesign v2 (OR-67): tick boxes + ONE button per card ──
  linkN: (n) => `Link ${n} ${plural(n, 'record', 'records')} to ABHA`,
  tryAgain: 'Try again', // all ticked rows are retries: no confirm
  noneTicked: 'Tick the records to link.', // why the button is off at 0 ticked
  nothingToLink: 'Nothing to link yet', // button text when no row on the card can be linked at all (all blocked / none held)
  allLinkedPill: '✓ All linked', // state pill
  showRecords: 'Show records ▾',
  hideRecords: 'Hide records ▴',
  selectAll: (n) => `Select all ${n} not linked yet`, // long lists: ticks the hidden rows too
};

// ── Doctor for patient (OR-59; label OR-64) ─────────────────────────────────────────────────────────────
// A visit with no doctor (LAB walk-in, nurse-only) cannot be linked until staff pick one. The pick is
// saved only on what is sent to ABHA; the visit, reports and accounts stay as they are.
export const PRACTITIONER = {
  label: 'Doctor for patient', // OR-64
  hint: "Only shown on the ABHA record. The visit isn't changed.",
  pickReason: 'Pick the doctor to show on this record', // why Confirm / the upload button / the invoice form's Link to ABHA is disabled; also under the invoice's highlighted picker (OR-106)
  noDoctorNote: 'No doctor on this visit', // small note on the encounter card
  // The organisation has no active doctor to pick: in place of the picker (= server NO_DOCTORS_IN_ORGANISATION message, §4.7 A3)
  noDoctors: 'Add a doctor in Staff to link these records.',
  // Link all mixing records linked before with new ones: the picked doctor applies to the new ones only (R3)
  keepsDoctor: 'Already-linked records keep their doctor.',
};

const CANT_UNDO = "This can't be undone.";

// ── Confirm modals (rows 52, 57, 58) ─────────────────────────────────────────────────
export const CONFIRM = {
  cancel: 'Cancel',
  oneTitle: LINK_COPY.confirmCta, // 'Link to ABHA'
  oneCta: LINK_COPY.confirmCta,
  allTitle: (n) => `Link ${n} ${plural(n, 'record', 'records')} to ABHA`,
  allCta: (n) => `Link ${n} ${plural(n, 'record', 'records')}`,
  uploadCta: 'Upload and link to ABHA',
  // redesign v2 (mockup frame 9): shorter bodies, "can't".
  oneBodyShort: (typeLabel, phrase) => `Link the ${typeLabel} of ${phrase} to their ABHA? It will appear in the patient's ABHA app. ${CANT_UNDO}`,
  allBodyShort: (phrase) => `These records of ${phrase} will appear in the patient's ABHA app. ${CANT_UNDO}`,
  uploadBodyShort: (typeLabel, phrase) => `Link this ${typeLabel} to ${phrase} and to their ABHA? It will appear in the patient's ABHA app. ${CANT_UNDO}`,
};

// ── Records panel + preview (rows 22-29, 63, 68-70) ───────────────────────────────────
export const REPLACE_FILE = 'Replace file'; // pencil tooltip + aria (OR-10) AND the panel's Replace button (row 25a/56)
export const ROWS = {
  savedInApp: (whenText) => `Saved in ArogyaKhata · ${whenText}`, // row 23
  uploaded: (whenText, fileName) => `Uploaded ${whenText}${fileName ? ` · ${fileName}` : ''}`, // row 29
  linkedCopy: (whenText) => `Linked copy · ${whenText}`, // row 24
  linkedCopyTip: 'Opens the exact copy linked to ABHA', // row 25
  download: 'Download',
  replacedByStaff: REPLACED_BY_STAFF, // row 28 (tag on the row)
  replace: REPLACE_FILE,
  replaceLockedLinked: "Can't replace: already linked to ABHA.", // row 26
  replaceLockedLinking: "Can't replace: linking to ABHA now.", // row 27
};
export const PREVIEW = {
  close: 'Close',
  previous: '‹ Previous',
  next: 'Next ›',
  fileOf: (n, m) => `File ${n} of ${m}`, // row 69
  loadError: 'Could not open this file. Please try again.',
  // A PDF cannot be shown in the page on this device (canInlinePdf false): Download still works.
  noInlinePdf: 'This device cannot show PDFs here. Use Download to open it.',
  // OR-19: OP Consultation with no prescription - View opens a summary of what will be linked.
  opConsultSummary: {
    title: 'What will be linked',
    titleLinked: 'What was linked', // same summary once the slot is linking / linked (past tense)
    complaint: 'Complaint',
    diagnosis: 'Diagnosis',
    doctor: 'Doctor',
    date: 'Date',
  },
};

// ── Checklist rows (redesign v2, OR-65/67): one vocabulary of row states ──────────────
// Invoice rows (bill not filled in / Fill in bill / Open bill) live in INVOICE.card.row: the OR-20 guard
// keeps invoice wording in INVOICE. State is always dot + words, never colour alone.
export const ROW = {
  notLinkedYet: 'Not linked yet',
  linked: (whenText) => (whenText ? `Linked ${whenText}` : LINK_STATUS.LINKED),
  linking: LINK_STATUS.NOTIFIED, // 'Linking…'
  paused: pausedSentence,
  failed: LINK_STATUS.FAILED, // 'Link failed'
  failedHint: LINK_COPY.retryError, // 'Could not link. Please try again.'
  notApproved: 'Not approved yet', // + NOT_APPROVED.reason under it
  // OR-79: an OP Consultation with no approved prescription and no staff file (slot blocked_reason RX_REQUIRED). The
  // row's whole text, also the card's message when the link route answers 422 RX_REQUIRED (the server's message is not shown).
  emptyRecord: "Record is empty. It can't be linked to ABHA.",
  view: 'View',
  viewN: (n) => `View (${n})`,
  tick: (name) => `Link ${name}`, // aria-label of the tick box
  showMore: (n) => `Show ${n} more ▾`,
  showLess: 'Show less ▴',
};

// ── Record-type pill strip (redesign v2, OR-69/73): all 8 types on every card, fixed order ──
export const STRIP = {
  listLabel: 'Record types', // aria-label of the 8-pill list
  // Legend line under the count line.
  legend: {
    label: 'Record types:',
    inApp: 'in ArogyaKhata',
    linked: '✓ linked',
    missing: '+ not added yet',
    notApplicable: 'greyed: not for this visit',
    tick: 'tick a row to choose what to link',
  },
  // One word per pill state (tooltip + aria). Colour never carries the state alone.
  state: {
    linked: LINK_STATUS.LINKED,
    linking: ROW.linking,
    paused: SLOT_STATE_LABEL.PAUSED,
    failed: ROW.failed,
    notApproved: ROW.notApproved,
    inApp: 'In ArogyaKhata',
    missing: 'Not added yet',
  },
  // aria-label of a pill: "Diagnostic Report: Linked" / "Diagnostic Report, 3: In ArogyaKhata" (count of a multi type).
  pillLabel: (typeLabel, stateWord, count) => (count > 1 ? `${typeLabel}, ${count}: ${stateWord}` : `${typeLabel}: ${stateWord}`),
  addFile: (typeLabel) => `Add file: ${typeLabel}`, // a missing pill (Invoice: INVOICE.card.stripFill)
  addAnother: (typeLabel) => `Add another ${typeLabel}`, // the + on a multi type that already has one
  // A type outside the visit kind (not in `allowedTypes`): greyed, not clickable, this tooltip + aria and no inline text.
  // Invoice applies to every visit. Keys = the types the server leaves out of some kind (types.py RECORD_TYPES_BY_KIND):
  // OPD greys Discharge Summary; IPD greys OP Consultation and Prescription. Each has its own sentence and there is NO
  // generic fallback (OR-78): a type the server newly leaves out needs the owner's wording here, and HipRecordTypeStrip.test fails until it has it.
  notApplicable: {
    OPConsultation: `${hiTypeLabel('OPConsultation')} is only for OPD visits`,
    Prescription: `${hiTypeLabel('Prescription')} is only for OPD visits`,
    DischargeSummary: `${hiTypeLabel('DischargeSummary')} is only for admissions`,
  },
};

// ── INCOMPLETE_RECORD (422): say what is missing and where to fix it (redesign v2) ────
// The server's `missing` is a list of codes (BE publishing/service.py missing_identity_fields; "practitioner" =
// no doctor picked). One sentence per record: used by the card (its button and the inline panel's link) and the Invoice drawer.
// UNVERIFIED (ask PO / Business at the verdict; text only, no deep links): which screen a receptionist uses to edit a patient's
// date of birth / gender, and that the organisation name is edited in Settings.
const fixPatient = (n) => `Add ${n === 1 ? 'it' : 'them'} in the patient's details, then try again.`;
const fixSettings = () => 'An admin can add it in Settings.';
const fixStaff = () => 'An admin can add it in Staff.';
const fixPicker = () => `Pick the ${PRACTITIONER.label.toLowerCase()}.`;
export const MISSING = {
  patient_name: { what: "the patient's name", fix: fixPatient },
  patient_gender: { what: "the patient's gender", fix: fixPatient },
  patient_dob: { what: "the patient's date of birth", fix: fixPatient },
  clinic_name: { what: "the organisation's name", fix: fixSettings },
  practitioner_name: { what: "the doctor's name", fix: fixStaff },
  practitioner: { what: 'a doctor for this record', fix: fixPicker },
};
const joinAnd = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
const INCOMPLETE_FALLBACK = "Can't link yet: some details are missing.";
const isMissingCode = (code) => Object.prototype.hasOwnProperty.call(MISSING, code);

/**
 * "Can't link yet: the patient's date of birth is missing. Add it in the patient's details, then try again."
 * `missing` = err.missing; `serverMessage` = err.userMessage. Any code this file does not know (or no codes at
 * all) falls back to the server's own message, so a new server code is never hidden or guessed at.
 */
export function incompleteRecordText(missing, serverMessage) {
  if (!Array.isArray(missing) || missing.length === 0 || !missing.every(isMissingCode)) {
    return serverMessage || INCOMPLETE_FALLBACK;
  }
  const codes = [...new Set(missing)];
  const patientCount = codes.filter((code) => MISSING[code].fix === fixPatient).length;
  const fixes = [...new Set(codes.map((code) => MISSING[code].fix))].map((fix) => fix(patientCount));
  const verb = codes.length === 1 ? 'is' : 'are';
  return `Can't link yet: ${joinAnd(codes.map((code) => MISSING[code].what))} ${verb} missing. ${fixes.join(' ')}`;
}

// ── Patient page (redesign v2, OR-75): every visit / admission of one patient, last 12 months ──
export const PATIENT = {
  back: '← Back to the list',
  // "Your list filters are kept: Not linked yet · Last 3 days · All visits" (filtersText joined by the page).
  filtersKept: (filtersText) => `Your list filters are kept: ${filtersText}`,
  headerLine: (uhid, mobileMasked) => (mobileMasked ? `UHID ${uhid} · Mobile ${mobileMasked}` : `UHID ${uhid}`),
  range12: (rangeText) => `Last 12 months · ${rangeText}`, // rangeText = LIST.rangeDash(...)
  // "40 visits and admissions in the last 12 months · showing the newest 10 · newest first"
  countLine: (total, shown) => (shown < total
    ? `${visitsAndAdmissions(total)} in the last 12 months · showing the newest ${shown} · newest first`
    : `${visitsAndAdmissions(total)} in the last 12 months · newest first`),
  showOlder: (n) => `Show ${n} older ${plural(n, 'visit', 'visits')} ▾`, // appends the next page
  shownOf: (shown, total, oldestText) => `Showing ${shown} of ${total} · oldest shown: ${oldestText}`,
  monthHeader: (monthText, n) => `${monthText} · ${n}`, // "October 2026 · 2"
  // On every card (name link + small text link) - hidden inside the patient page.
  viewAll: 'View all records for this patient →',
  viewAllLabel: 'View all records for this patient', // aria / tooltip of the name link
  noAbha: 'This patient has no ABHA on file, so there is nothing to link.',
  empty: 'No visits or admissions in the last 12 months.',
  notFound: 'This patient was not found.',
};

// ── Inline Add file / Replace panel (rows 31-56; OR-82 replaced the drawer) ───────────
export const UPLOAD = {
  // OR-16: outside papers are NOT linked - no tick box, a guidance line instead.
  // Wording is the dev's from "ask the patient to upload it in their ABHA app".
  // redesign v2 (OR-64): shorter, and drawn in the Add file panel only (never in Replace, OR-11).
  outsideGuidance: 'From another organisation? Ask the patient to add it in their ABHA app.',
  // OR-18: Prescription and Diagnostic Report take ONE file.
  singleFileHint: 'Choose one file. If the record has several pages, join them into one PDF first.',
  // OR-52: the Diagnostic Report name is the test name only (add and replace).
  titleHelperDiagnostic: "Type the test name only, e.g. CBC. Don't type results.",
  // OR-48: "<Type> - <date>" (plain dash, OR-47); a Diagnostic Report takes the file name without its extension instead.
  titleDefault: (typeLabel, dateText) => `${typeLabel} - ${dateText}`, // row 40, OR-48 plain dash
  chooseFileButton: 'Choose file',
  fileRules: 'PDF, JPG or PNG · up to 10 MB', // row 43
  chooseAnother: 'Choose another file', // row 44
  sizeError: (mbText) => `This file is ${mbText} MB. Choose a file up to 10 MB.`, // row 45
  uploadError: 'Could not upload. Check the connection and try again.', // row 46
  cta: 'Upload and link to ABHA', // row 50
  cancel: 'Cancel', // also for Replace
  // Not in wording-review.md (all of the below).
  ctaBusy: 'Uploading…',
  uploadedNote: 'The file is uploaded. Only linking to ABHA is left.', // card line when the upload went through but the link did not
  // Client-side file checks (the server enforces the same limits).
  fileTypeError: (name) => `${name} is not a PDF, JPG or PNG file. Choose a PDF, JPG or PNG file.`,
  fileEmptyError: (name) => `${name} is empty. Choose another file.`,
  tooManyFiles: (n) => `Choose up to ${n} files at a time.`,
  recordFull: (n) => `A record can hold up to ${n} files. Remove some or use Replace file.`,
  recordTooBig: (mb) => `The files of one record can be up to ${mb} MB in total.`,

  // ── redesign v2 (OR-65/73): the panel opens on the chosen type - no type tiles, no Patient / Attach box ──
  titleFor: (typeLabel) => `Add ${typeLabel}`,
  stepName: 'Name shown in the ABHA app',
  stepDoctor: `${PRACTITIONER.label} (required)`, // only on a visit with no doctor
  nameHint: 'Shown to the patient in the ABHA app. No results or diagnosis.',
  fileReason: 'Choose a file first.', // why the panel's button is off while it holds no file
};
export const REPLACE = {
  title: (typeLabel) => `Replace ${typeLabel}`, // row 53
  replacing: 'Replacing',
  notLinkedYet: 'not linked yet',
  body: 'The new file is linked instead of the one ArogyaKhata made. The original stays unchanged in the app. You can replace it until it is linked to ABHA.', // row 54
  // Row 55 (lab: "replaces all n lab report files") is retired: a Diagnostic Report slot is ONE
  // report file now (A1 / OR-36), so only the generic line below can ever apply.
  replacesAll: (n) => `The new files replace all ${n} files.`, // multi-file types (OR-12)
  ctaBusy: 'Replacing…',
  footerNote: 'Linking to ABHA is a separate step.', // row 56
  cta: REPLACE_FILE,
};
// ── The inline panel itself (OR-82; UX spec ux-inline-add-file.md section 3) ──
export const INLINE = {
  // One panel per card: the other add pills, the preview's pencil and the panel's status line all say this.
  finishFirst: 'Finish or cancel the file you are adding first.',
  removeFile: (name) => `Remove ${name}`, // aria-label + tooltip of a file's ✕
  chosenAnnounce: (name, typeLabel, ctaLabel) => `${name} chosen for ${typeLabel}. Check the name, then choose ${ctaLabel}.`, // screen-reader line
  replacedAnnounce: (typeLabel) => `${typeLabel} file replaced. Linking to ABHA is a separate step.`, // screen-reader line
};

// ── Invoice form (HipInvoiceDrawer, INV-F1) ── (from the approved invoice-form mockup,
// OR-20..OR-28, OR-37, OR-43). Verb "link", never "send". Amounts are formatted by formatPaise.
export const INVOICE_TYPE_LABEL = { // NRCeS ndhm-invoice-types display names (OR-25)
  '00': 'Consultation',
  '01': 'Pharmacy',
  '02': 'IPD',
  '03': 'OPD',
  '99': 'Others',
};
export const INVOICE = {
  title: 'Invoice',
  sep: ' - ', // OR-47: plain ASCII dash between facts, never a middle dot
  subtitle: "Fill in the bill that will be linked to the patient's ABHA.",
  linkedSubtitle: (whenText) => `Linked to the patient's ABHA on ${whenText}.`,
  linkedNote: "This invoice has been linked to ABHA, so it can't be changed.",
  notLinked: 'Not linked',
  unsaved: 'Unsaved changes',
  linkedChip: LINK_STATUS.LINKED,
  // A frozen invoice whose link failed (or never started): its read-only form offers ONE retry (redesign v2 R-F4).
  retryLink: RETRY_LINK,
  failedChip: LINK_STATUS.FAILED, // 'Link failed'
  prefillNote: {
    RECEIPT: 'Filled in from the OPD receipt. Check the lines and edit them if needed.',
    FINAL_BILL: 'Filled in from the final bill. Check the lines and edit them if needed.',
  },
  loading: LINK_COPY.panelLoading, // 'Loading…'
  loadError: 'Could not open this invoice. Please try again.',
  patient: 'Patient',
  visit: { OPD: 'Visit', IPD: 'Admission' },
  doctor: 'Doctor',
  billDate: 'Bill date',
  billDateFrom: { OPD: 'From the visit', IPD: 'From the admission (discharge date)' },
  billType: 'Bill type',
  billNumber: 'Bill number',
  billNumberRequired: 'required',
  billNumberHelper: 'Type it as printed on the bill.',
  items: (n) => `Items (${n})`,
  addLine: '+ Add line',
  removeLine: (n) => `Remove line ${n}`, // aria + tooltip of the ✕
  removeOnlyLine: 'An invoice needs at least one line.', // tooltip of the disabled ✕
  col: {
    n: '#', item: 'Item', rate: 'Rate ₹', discount: 'Discount ₹', cgst: 'CGST ₹', sgst: 'SGST ₹', optional: 'optional',
  },
  descriptionCounter: (n, max) => `${n}/${max}`,
  lineFieldLabel: (n, col) => `Line ${n} ${col}`, // aria-label of each line input
  moreErrors: (n) => `+${n} more`,
  needsVerify: CARD.uploadNeedsVerify, // 'Verify ABHA first' - reason under a disabled Link to ABHA
  net: 'Net',
  taxes: 'Taxes (CGST + SGST)',
  gross: 'Gross',
  totalsHelp: 'Net = rates - discounts. Gross = Net + taxes. Worked out for you.', // OR-47 plain dash
  totalsPending: 'Totals show once every line is valid.',
  errorsTitle: 'Fix these first',
  lineError: (n, message) => `Line ${n}: ${message}`,
  saveDraft: 'Save draft',
  saving: 'Saving…',
  linkToAbha: LINK_COPY.confirmCta, // 'Link to ABHA'
  linking: LINK_STATUS.NOTIFIED, // 'Linking…'
  cancel: 'Cancel',
  close: 'Close',
  err: { // client checks, same rules as the server (INV-B3 invoice_rules)
    billNumber: 'Enter the bill number.',
    billType: 'Choose the bill type.',
    description: 'Enter the item.',
    rateRequired: 'Enter the rate.',
    negative: "Amounts can't be less than 0.",
    notAmount: 'Enter amounts like 150 or 150.50.',
    lineAmount: 'Enter a rate or a discount greater than 0.',
    discountsOverRates: 'The discounts add up to more than the rates.',
    totalNotPositive: 'The invoice total must be more than 0.',
    saveFailed: 'Could not save the invoice. Please try again.',
    linkFailed: LINK_COPY.retryError, // 'Could not link. Please try again.'
    reloaded: 'This invoice was changed by someone else. The latest copy is shown; check it and try again.',
  },
  confirmLinkTitle: LINK_COPY.confirmCta, // 'Link to ABHA'
  confirmLinkBody: (typeLabel, billNumber, patientName, nLines) => (
    `Link the ${typeLabel} invoice ${billNumber} (${nLines} ${plural(nLines, 'item', 'items')}) of ${patientName} to their ABHA? It will appear in the patient's ABHA app. This cannot be undone.`),
  confirmLinkTotals: (net, taxes, gross) => `Net ${net} · Taxes ${taxes} · Gross ${gross}`, // on-screen info line: dots (OR-53/OR-60), a dash would read as subtraction
  clearGstTitle: 'Remove CGST and SGST?', // OR-22
  clearGstBody: (typeLabel) => `CGST and SGST apply to Pharmacy bills only. Changing the bill type to ${typeLabel} clears the CGST and SGST you typed.`,
  clearGstCta: 'Change and clear',
  discardTitle: 'Discard changes?',
  discardBody: 'You changed this invoice. Close without saving and lose your changes?',
  discardCta: 'Discard changes',
  keepEditing: 'Keep editing',
  // Invoices on the encounter card (chips, Records rows, delete) - INV-F2. Facts are joined
  // with the plain " - " above (OR-47); amounts are formatted by formatPaise. Kept inside INVOICE: the OR-20 guard
  // test allows invoice wording nowhere else in this file.
  card: {
    // "Invoice - Pharmacy - Bill PH/221" (the amount follows, in its own no-wrap span). invoice = slot.invoice.
    rowHead: (invoice) => [
      hiTypeLabel(TYPE_INVOICE), INVOICE_TYPE_LABEL[invoice.invoiceType], `Bill ${invoice.billNumber}`,
    ].join(INVOICE.sep),
    deleteDraft: 'Delete draft',
    // Delete a DRAFT (a linked invoice has no delete).
    deleteTitle: 'Delete this draft?',
    deleteBody: (billNumber) => `The draft of invoice ${billNumber} will be deleted. This cannot be undone.`,
    deleteCta: 'Delete draft',
    keepDraft: 'Keep draft',
    deleteFailed: 'Could not delete the draft. Please try again.',
    // redesign v2: checklist row of an invoice (no tick box - it links from its own form, OR-70).
    row: {
      notFilled: 'Bill details not filled in',
      fromReceipt: 'Taken from the OPD receipt. Check it, then link it.',
      fromFinalBill: 'Taken from the final bill. Check it, then link it.',
      filled: 'Bill filled in',
      fillIn: 'Fill in bill →',
      open: 'Open bill',
    },
    // The Invoice pill of the record-type strip when no bill exists yet (aria + tooltip).
    stripFill: (typeLabel) => `Fill invoice: ${typeLabel}`,
  },
};
